import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Save, X, Check, AlertTriangle, Info, BookOpen, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { computeRecipeTotals, deriveArraysFromLine } from "../lib/recipe";
import {
  Field, Input, Select, ToggleGroup, MultiSelect,
  PrimaryButton, QuietButton
} from "./UI";
import { RecipeBuilder, UNIT_OPTIONS, blockEnterSubmit } from "./RecipeBuilder";

const f = (v) => v == null || v === "" ? 0 : Number(v) || 0;

/* ============================================================
 * SaveToLibraryModal — Polish prompt Section 4 (brought forward
 * into Section 1 build per user instruction).
 *
 * Promotes a tender line into a NEW master library item. Pre-fills
 * description / unit / trade / phase tags / item type / recipe from
 * the source line (or from the active drawer state when invoked
 * from inside the recipe drawer). The user can edit anything before
 * saving — including adding/removing labour rows, materials rows,
 * plant rows, subby rows freely. Same blocker rules as the Library
 * Edit validation modal apply: description, unit, trade, ≥1 tag.
 *
 * The original tender line retains its current snapshot — the new
 * library item evolves independently. Per Master Brief 7.4.
 *
 * Props:
 *   open               — controls visibility
 *   onClose            — called when user dismisses
 *   sourceLine         — the tender line being promoted (read-only here)
 *   sourceArrays       — optional override for the recipe arrays;
 *                        when present (e.g. live drawer state), used as
 *                        the recipe pre-fill instead of deriving from
 *                        the line's snapshot/cells
 *   tenderId           — for audit traceability (optional)
 *   masterPhases       — phase tag suggestions (array of strings)
 *   labourRates        — for Trade dropdown + recipe builder
 *   materials          — for material autocomplete in recipe builder
 *   onCreated(libItem) — fired with the new library doc on success
 * ============================================================ */
export function SaveToLibraryModal({
  open, onClose, sourceLine, sourceArrays, tenderId,
  masterPhases = [], labourRates = [], materials = [],
  onCreated, onApplyToLine,
}) {
  const [form, setForm] = useState({
    description: "",
    unit: "",
    trade_name: "",
    trade_code: "",
    phase_tags: [],
    item_type: "priced",
  });
  const [arrays, setArrays] = useState({
    recipe_labour: [], recipe_materials: [], recipe_plant: [], recipe_subby: []
  });
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // Option (b) — opt-in: also apply this recipe to the source tender line.
  // Defaults checked (most common user intent: use the recipe in this tender too).
  // When unchecked, only the new library item is created and the source line
  // keeps its existing snapshot — preserves the strict separation rule.
  const [applyToLine, setApplyToLine] = useState(true);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  // Initialise on open — pre-fill from the source line + any active drawer arrays
  useEffect(() => {
    if (!open || !sourceLine) return;
    const seedArrays = sourceArrays && (
      (sourceArrays.recipe_labour || []).length ||
      (sourceArrays.recipe_materials || []).length ||
      (sourceArrays.recipe_plant || []).length ||
      (sourceArrays.recipe_subby || []).length ||
      sourceArrays.flat_material
    )
      ? sourceArrays
      : deriveArraysFromLine(sourceLine);

    setArrays({
      recipe_labour:    [...(seedArrays.recipe_labour    || [])],
      recipe_materials: [...(seedArrays.recipe_materials || [])],
      recipe_plant:     [...(seedArrays.recipe_plant     || [])],
      recipe_subby:     [...(seedArrays.recipe_subby     || [])],
      flat_material:    seedArrays.flat_material ? { ...seedArrays.flat_material } : null,   // LT-36
    });

    // Pre-fill metadata. trade_name comes from the snapshot or the line cell;
    // if a labour rate match exists in the seed recipe, prefer that trade.
    const firstLabour = (seedArrays.recipe_labour || [])[0];
    const seedTrade = firstLabour?.trade_name || sourceLine.trade_name || "";
    setForm({
      description: sourceLine.description || "",
      unit: sourceLine.unit || "",
      trade_name: seedTrade,
      trade_code: "",
      phase_tags: [],  // user must pick at least one — leave blank to force the choice
      item_type: sourceLine.type === "provisional_sum" ? "provisional_sum"
        : sourceLine.type === "prelim" ? "prelim"
        : "priced",
    });
    setShowValidation(false);
    setApplyToLine(true);  // reset to default each time the modal opens
  }, [open, sourceLine, sourceArrays]);

  const totals = useMemo(() => computeRecipeTotals(arrays), [arrays]);

  // Trade dropdown options sourced from labour_rates
  const tradeOptions = useMemo(() =>
    (labourRates || [])
      .filter((lr) => !lr.is_archived)
      .map((lr) => ({
        value: lr.id,
        label: lr.trade_code ? `${lr.trade_name} (${lr.trade_code})` : lr.trade_name,
        rate: lr,
      })),
    [labourRates]
  );
  const selectedTrade = (labourRates || []).find((lr) => lr.trade_name === form.trade_name);

  const checks = useMemo(() => {
    const tagsCount = (form.phase_tags || []).length;
    const labourEmpty = (arrays.recipe_labour || []).length === 0;
    const materialsEmpty = arrays.flat_material ? !(f(arrays.flat_material.rate) > 0) : (arrays.recipe_materials || []).length === 0;
    const plantEmpty = (arrays.recipe_plant || []).length === 0;
    return [
      { label: "Description", severity: "required", pass: !!form.description?.trim(),
        detail: form.description?.trim() ? null : "Required — what is this item?" },
      { label: "Unit", severity: "required", pass: !!form.unit,
        detail: form.unit ? null : "Pick a recipe yield unit." },
      { label: "Trade", severity: "required", pass: !!form.trade_name,
        detail: form.trade_name ? null : "Pick a trade — required so the item surfaces in the right tender contexts." },
      { label: "At least one Phase / Category tag", severity: "required", pass: tagsCount > 0,
        detail: tagsCount > 0 ? `${tagsCount} tag${tagsCount === 1 ? "" : "s"} added.` : "Tag the item against ≥1 phase or category." },
      { label: "Labour section", severity: "warning", pass: !labourEmpty,
        detail: labourEmpty ? "No labour entries — confirm intentional." : null },
      { label: "Materials section", severity: "warning", pass: !materialsEmpty,
        detail: materialsEmpty ? "No materials entries — confirm intentional." : null },
      { label: "Plant section", severity: "info", pass: !plantEmpty,
        detail: plantEmpty ? "No plant entries — most items don't need any." : null },
    ];
  }, [form, arrays]);
  const blockers = checks.filter((c) => c.severity === "required" && !c.pass);
  const canSave = blockers.length === 0;

  const handleSave = async () => {
    if (!canSave) {
      setShowValidation(true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        description: form.description.trim(),
        unit: form.unit,
        phase_tags: form.phase_tags,
        category_tags: [],
        trade_code: form.trade_code || null,
        trade_name: form.trade_name || null,
        trade_manual_override: !!form.trade_name,  // user explicitly picked
        item_type: form.item_type,
        notes: null,
        recipe_labour: (arrays.recipe_labour || []).map((e) => ({
          ...e, hours_per_unit: f(e.hours_per_unit), hourly_rate: f(e.hourly_rate)
        })),
        recipe_materials: (arrays.recipe_materials || []).map((e) => ({
          ...e, qty_per_unit: f(e.qty_per_unit), rate: f(e.rate)
        })),
        recipe_plant: (arrays.recipe_plant || []).map((e) => ({
          ...e, cost_per_unit: f(e.cost_per_unit)
        })),
        recipe_subby: (arrays.recipe_subby || []).map((e) => ({
          ...e, cost_per_unit: f(e.cost_per_unit)
        })),
        // LT-36 — Flat Material Rate travels onto the new master as-is.
        flat_material_enabled: !!arrays.flat_material,
        flat_material_rate: arrays.flat_material ? f(arrays.flat_material.rate) : null,
        flat_material_note: arrays.flat_material?.note || null,
      };
      const res = await api.post("/library", payload);
      const lib = res.data;
      // Option (b) — fire the apply-to-line callback if the user opted in.
      // The page hands this hook a function that calls applyRecipeArraysToLine
      // and updates the in-memory phases tree so the Tender autosave persists it.
      if (applyToLine) {
        onApplyToLine?.({ line: sourceLine, arrays });
      }
      const lineRefBit = sourceLine?.ref ? ` to line ${sourceLine.ref}` : "";
      toast.success(
        <span className="inline-flex items-center gap-1.5">
          {applyToLine ? `Created ${lib.sku} and applied${lineRefBit}` : `Created ${lib.sku} in library`}
        </span>,
        {
          duration: 5000,
          action: {
            label: (
              <span className="inline-flex items-center gap-1">
                <ExternalLink size={11} /> View
              </span>
            ),
            onClick: () => window.open(`/library/${lib.id}`, "_blank"),
          },
        }
      );
      onCreated?.(lib);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[65] bg-ink/40 flex items-start justify-center pt-12 pb-8 px-4 overflow-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="save-to-library-modal-backdrop"
    >
      <div
        className="w-full bg-bg border border-line rounded-lg shadow-card flex flex-col"
        style={{ maxWidth: 920, maxHeight: "calc(100vh - 80px)" }}
        data-testid="save-to-library-modal"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-3.5 border-b border-line bg-surface">
          <div className="flex items-center gap-2.5">
            <BookOpen size={14} className="text-accent" />
            <h2 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>
              Save as new library item
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="save-to-library-close"
            className="text-ink-3 hover:text-ink p-1.5 rounded-md hover:bg-surface-2"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </header>

        {/* Body */}
        <div
          className="flex-1 overflow-auto px-6 py-5"
          onKeyDown={blockEnterSubmit}
        >
          <p className="text-ink-3 mb-5" style={{ fontSize: 12.5 }}>
            Pre-filled from this tender line — edit anything before saving. The original line keeps its current snapshot;
            the new library item evolves independently.
          </p>

          {/* Top metadata grid */}
          <div className="grid grid-cols-2 gap-x-5 gap-y-4 mb-5">
            <div className="col-span-2">
              <Field label="Description" required>
                <Input
                  value={form.description}
                  onChange={set("description")}
                  placeholder="e.g. Plasterboard wall — finish to client face"
                  data-testid="stl-description"
                />
              </Field>
            </div>

            <Field label="SKU" hint="Auto-generated on save (LIB-XXXX). Immutable.">
              <Input
                value="Will be assigned on save"
                onChange={() => {}}
                mono
                readOnly
                data-testid="stl-sku-readonly"
              />
            </Field>

            <Field label="Item type" required>
              <ToggleGroup
                value={form.item_type}
                onChange={set("item_type")}
                options={[
                  { value: "priced",          label: "Priced" },
                  { value: "provisional_sum", label: "Prov. sum" },
                  { value: "prelim",          label: "Prelim" }
                ]}
              />
            </Field>

            <Field label="Unit (recipe yield)" required>
              <Select
                value={form.unit}
                onChange={set("unit")}
                options={UNIT_OPTIONS}
                placeholder="Pick…"
                data-testid="stl-unit"
              />
            </Field>

            <Field label="Trade" required hint="Sourced live from Labour Rates.">
              <Select
                value={selectedTrade?.id || ""}
                onChange={(id) => {
                  const lr = (labourRates || []).find((x) => x.id === id);
                  if (!lr) return;
                  setForm((s) => ({ ...s, trade_name: lr.trade_name, trade_code: lr.trade_code || "" }));
                }}
                options={tradeOptions}
                placeholder={tradeOptions.length ? "Pick a trade…" : "No labour rates yet"}
                data-testid="stl-trade"
              />
            </Field>

            <div className="col-span-2">
              <Field
                label="Phase / Category tags"
                required
                hint="Multi-select — type to create a custom tag."
              >
                <MultiSelect
                  value={form.phase_tags}
                  onChange={set("phase_tags")}
                  options={masterPhases || []}
                  tone="accent"
                  placeholder="Tag a phase or category"
                  allowCustom
                />
              </Field>
            </div>
          </div>

          {/* Recipe builder */}
          <div className="border-t border-line pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
                Recipe (per {form.unit || "unit"})
              </h3>
              <span className="text-ink-3" style={{ fontSize: 11.5 }}>
                Add / remove rows freely — pre-fill is a starting point, not a constraint.
              </span>
            </div>
            <RecipeBuilder
              arrays={arrays}
              onChange={setArrays}
              totals={totals}
              unit={form.unit}
              labourRates={labourRates}
              materials={materials}
              showSubby
            />
          </div>

          {/* Validation list (shown after a failed save attempt OR proactively) */}
          {showValidation && (
            <div
              className="mt-5 p-4 rounded-md border border-line bg-surface"
              data-testid="stl-validation-list"
            >
              <p className="text-ink-2 mb-3" style={{ fontSize: 12.5, fontWeight: 600 }}>
                Required fields missing
              </p>
              <ul className="space-y-2">
                {checks.map((c, i) => {
                  const ok = !!c.pass;
                  let bg, fg, Icon;
                  if (c.severity === "required") {
                    if (ok) { bg = "#E8EFE9"; fg = "#2F4F3E"; Icon = Check; }
                    else    { bg = "#F4E6E4"; fg = "#9B3A37"; Icon = AlertTriangle; }
                  } else if (c.severity === "warning") {
                    bg = ok ? "#E8EFE9" : "#FEF3E2"; fg = ok ? "#2F4F3E" : "#7A4C0A";
                    Icon = ok ? Check : AlertTriangle;
                  } else {
                    bg = ok ? "#E8EFE9" : "#EEF2F8"; fg = ok ? "#2F4F3E" : "#3D5478";
                    Icon = ok ? Check : Info;
                  }
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-2 px-2.5 py-1.5 rounded"
                      style={{ background: bg, color: fg, fontSize: 12 }}
                    >
                      <Icon size={12} style={{ marginTop: 2, flex: "0 0 auto" }} />
                      <span style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{c.label}</span>
                        {c.detail && <span className="block opacity-80 mt-0.5" style={{ fontSize: 11 }}>{c.detail}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="px-6 py-3.5 border-t border-line bg-surface flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <label
              className="inline-flex items-center gap-2 cursor-pointer select-none"
              data-testid="stl-apply-to-line-label"
            >
              <input
                type="checkbox"
                checked={applyToLine}
                onChange={(e) => setApplyToLine(e.target.checked)}
                data-testid="stl-apply-to-line-checkbox"
                className="w-3.5 h-3.5 rounded border-line accent-accent cursor-pointer"
                style={{ accentColor: "#2F4F3E" }}
              />
              <span className="text-ink-2" style={{ fontSize: 12 }}>
                Also apply this recipe to{sourceLine?.ref ? ` line ${sourceLine.ref}` : " the line"}
              </span>
            </label>
            {!canSave && (
              <span className="text-ink-3 truncate" style={{ fontSize: 11.5 }}>
                · {blockers.length} required field{blockers.length === 1 ? "" : "s"} missing
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <QuietButton onClick={onClose} data-testid="stl-cancel">Cancel</QuietButton>
            <PrimaryButton
              onClick={handleSave}
              disabled={saving}
              icon={saving ? Loader2 : Save}
              data-testid="stl-save"
            >
              Create library item
            </PrimaryButton>
          </div>
        </footer>
      </div>
    </div>
  );
}
